<script setup lang="ts">
import { ref, reactive } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useToast } from 'primevue/usetoast'
import Button from 'primevue/button'
import InputText from 'primevue/inputtext'
import Password from 'primevue/password'
import Checkbox from 'primevue/checkbox'
import AppCard from '@/components/ui/AppCard.vue'
import Divider from 'primevue/divider'
import Message from 'primevue/message'

const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const toast = useToast()

const loading = ref(false)
const error = ref<string | null>(null)

const form = reactive({
  username: '',
  password: '',
  remember_me: true,
})

const redirectPath = (route.query.redirect as string) ?? '/'

async function handleLogin(): Promise<void> {
  error.value = null
  loading.value = true

  try {
    await authStore.login(form.username, form.password, form.remember_me)
    toast.add({
      severity: 'success',
      summary: 'Bienvenido',
      detail: `Has iniciado sesión como ${authStore.user?.name}`,
      life: 3000,
    })
    router.push(redirectPath)
  } catch (err: unknown) {
    const axiosError = err as { apiError?: { message: string } }
    error.value = axiosError.apiError?.message ?? 'Credenciales inválidas. Verifica tu usuario y contraseña.'
    toast.add({
      severity: 'error',
      summary: 'Error de acceso',
      detail: error.value,
      life: 4000,
    })
  } finally {
    loading.value = false
  }
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Enter') {
    handleLogin()
  }
}
</script>

<template>
  <div class="min-h-screen bg-surface-50 dark:bg-surface-950 flex items-center justify-center px-4 py-12">
    <div class="w-full max-w-md">
      <AppCard class="p-8 animate-fade-in">
        <div class="text-center mb-8">
          <div class="flex items-center justify-center gap-2 mb-4">
            <i class="pi pi-briefcase text-4xl text-primary-600 dark:text-primary-400" />
            <h1 class="text-2xl font-bold text-surface-900 dark:text-surface-100">
              COTIZADOR VRO
            </h1>
          </div>
          <p class="text-surface-500 dark:text-surface-400">
            Inicia sesión para continuar
          </p>
        </div>

        <Message
          v-if="error"
          severity="error"
          :closable="true"
        >
          {{ error }}
        </Message>

        <form
          class="space-y-5"
          @submit.prevent="handleLogin"
        >
          <div>
            <label
              for="username"
              class="label-base"
            >Usuario</label>
            <InputText
              id="username"
              v-model="form.username"
              placeholder="tu_usuario"
              :disabled="loading"
              autocomplete="username"
              autofocus
              fluid
              @keydown="handleKeyDown"
            />
          </div>

          <div>
            <label
              for="password"
              class="label-base"
            >Contraseña</label>
            <Password
              v-model="form.password"
              input-id="password"
              placeholder="••••••••"
              :disabled="loading"
              autocomplete="current-password"
              fluid
              :toggle-mask="true"
              @keydown="handleKeyDown"
            />
          </div>

          <div class="flex items-center justify-between">
            <label class="flex items-center gap-2 cursor-pointer">
              <Checkbox
                v-model="form.remember_me"
                :binary="true"
              />
              <span class="text-sm text-surface-600 dark:text-surface-400">Recordarme</span>
            </label>
          </div>

          <Button
            type="submit"
            :loading="loading"
            :disabled="loading"
            class="w-full"
            size="large"
            label="Iniciar sesión"
          />
        </form>

        <Divider class="my-6" />
        <p class="text-center text-sm text-surface-500 dark:text-surface-400">
          COTIZADOR VRO &copy; {{ new Date().getFullYear() }}
        </p>
      </AppCard>
    </div>
  </div>
</template>

<style scoped>
/* Animación de entrada */
.animate-fade-in {
  animation: fade-in 0.3s ease-out;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>